import { required, useTranslate, type Identifier } from "ra-core";
import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";

import { firstOpenStage } from "../deals/stages";
import { useProjects } from "../projects/useCurrentProject";
import { leadSourceChoices } from "./sources";
import { ResponsibleInput } from "../sales/ResponsibleInput";

/**
 * The lead form: who, how to reach them, where they came from, the stage and
 * the responsible. The stage list is the lead pipeline of the lead's
 * project; the «won» stage is not offered (a lead gets there through
 * «В сделку»).
 */
export const LeadInputs = ({
  chooseProject = false,
}: {
  /** ask for the project (a new lead while the switcher is on «Все проекты») */
  chooseProject?: boolean;
}) => {
  const translate = useTranslate();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-1">
      <TextInput source="name" helperText={false} />
      <TextInput source="phone" type="tel" helperText={false} />
      <TextInput source="email" type="email" helperText={false} />
      <TextInput source="company_name" helperText={false} />
      <TextInput
        source="comment"
        multiline
        rows={3}
        helperText={false}
        className="md:col-span-2"
      />
      <SelectInput
        source="source"
        choices={leadSourceChoices(translate)}
        helperText={false}
        validate={required()}
      />
      <LeadStageInputs chooseProject={chooseProject} />
      <ResponsibleInput />
    </div>
  );
};

const LeadStageInputs = ({ chooseProject }: { chooseProject: boolean }) => {
  const { projects, pipelinesOf, stagesOf } = useProjects();
  const { setValue } = useFormContext();
  const projectId = useWatch({ name: "project_id" }) as Identifier | undefined;
  const stageId = useWatch({ name: "stage_id" }) as Identifier | undefined;
  const stages = useMemo(
    () =>
      projectId != null ? stagesOf(pipelinesOf(projectId, "lead")[0]?.id) : [],
    [projectId, pipelinesOf, stagesOf],
  );
  const choices = stages.filter(
    (s) => s.outcome !== "won" || String(s.id) === String(stageId),
  );

  // keep the stage inside the project's lead pipeline (also after ra-core's
  // Form resets its values)
  useEffect(() => {
    if (!stages.length) return;
    if (!stages.some((s) => String(s.id) === String(stageId))) {
      setValue("stage_id", firstOpenStage(stages).id, { shouldDirty: true });
    }
  }, [stages, stageId, setValue]);

  return (
    <>
      {chooseProject ? (
        <SelectInput
          source="project_id"
          choices={projects}
          optionText="name"
          optionValue="id"
          helperText={false}
          validate={required()}
        />
      ) : null}
      <SelectInput
        source="stage_id"
        choices={choices}
        optionText="name"
        optionValue="id"
        helperText={false}
        validate={required()}
      />
    </>
  );
};
